import { strTimeToMin } from "./utils";

/**
 * Types and layout helpers for the Stundenplan view (components/Timetable).
 * The shapes mirror the v2 routes `GET api/timetable/branch` and
 * `GET api/timetable/club`.
 */

/**
 * The Stundenplan (ticket #6) is built but not released yet. While this is
 * false, the Liste/Stundenplan switch on the branch and club course pages and
 * the branch colour setting are hidden, and the course pages always show the
 * list - also for `?view=stundenplan` links and for a view restored from the
 * list cache. Releasing it is flipping this to true.
 *
 * Until then it can be previewed with `?calendar=true`, see isTimetableEnabled.
 */
export const TIMETABLE_ENABLED = true;

const PREVIEW_KEY = "calendarPreview";

/**
 * Whether the Stundenplan is shown: released, or previewed in this browser.
 *
 * `?calendar=true` on any page turns the preview on and remembers it, so it
 * survives navigating to a course and back, the course page's own back link
 * (which drops the query) and the branch settings. `?calendar=false` ends it.
 * Read once per screen, so a screen does not change its mind while open.
 */
export function isTimetableEnabled(
  released: boolean = TIMETABLE_ENABLED
): boolean {
  if (released) return true;
  const param = new URLSearchParams(window.location.search).get("calendar");
  try {
    if (param === "true") window.localStorage.setItem(PREVIEW_KEY, "1");
    if (param === "false") window.localStorage.removeItem(PREVIEW_KEY);
    return window.localStorage.getItem(PREVIEW_KEY) === "1";
  } catch (err) {
    // Storage blocked (private mode, cookie settings): the URL alone decides.
    return param === "true";
  }
}

/** Card colour of a branch that has not picked one. */
export const DEFAULT_BRANCH_COLOR = "#5dade2";

export type TimetableLocation = {
  // null for courses without a location
  id: string | null;
  name: string;
};

export type TimetableSlot = {
  courseId: string;
  branchId: string;
  courseType: "single-course" | "block-course" | "infinity-course";
  title: string;
  sessionTitle: string | null;
  date: string; // "YYYY-MM-DD"
  weekDay: number; // 0 = Montag
  startTime: string; // "HH:MM"
  endTime: string;
  location: TimetableLocation;
  minAge: number | null;
  maxAge: number | null;
  places: number;
  freePlaces: number;
  full: boolean;
};

export type TimetableBranch = {
  id: string;
  name: string;
  color: string | null; // "#rrggbb", null = DEFAULT_BRANCH_COLOR
};

export type TimetableData = {
  start: string; // first shown day
  days: Array<string>; // the seven shown days
  branches: Array<TimetableBranch>;
  locations: Array<TimetableLocation>;
  slots: Array<TimetableSlot>;
};

export type PositionedSlot = TimetableSlot & {
  startMin: number;
  endMin: number;
  // side-by-side position for overlapping slots in the same room
  lane: number;
  lanes: number;
};

export type TimetableColumn = {
  location: TimetableLocation | null; // null: empty day, no room
  slots: Array<PositionedSlot>;
};

export type TimetableDay = {
  date: string;
  weekDay: number;
  columns: Array<TimetableColumn>;
};

const NO_LOCATION_KEY = "__none__";

export const locationKey = (location: TimetableLocation) =>
  location.id === null ? NO_LOCATION_KEY : location.id;

// Courses without a location are collected under "Ohne Raum" (customer wording).
export const locationName = (location: TimetableLocation) =>
  location.id === null ? "Ohne Raum" : location.name || "Unbenannter Ort";

/**
 * The rooms offered as filter in the club timetable: only the club's own
 * locations (Verein › Örtlichkeiten), not those a single branch created.
 * Club locations are copied into every branch under the same id, so the ids
 * of `club.locations` (from getClubFromId) pick them out of the room list.
 */
export const clubRooms = (
  locations: Array<TimetableLocation>,
  clubLocations: { [id: string]: unknown } | null | undefined
) =>
  locations.filter(
    (location) =>
      location.id !== null &&
      !!clubLocations &&
      Object.prototype.hasOwnProperty.call(clubLocations, location.id)
  );

/** The branch's colour, or the default one. */
export const branchColor = (data: TimetableData, branchId: string) => {
  const branch = data.branches.find((b) => b.id === branchId);
  return (branch && branch.color) || DEFAULT_BRANCH_COLOR;
};

/** "#rrggbb" as a translucent rgba(), for light card backgrounds. */
export const tint = (hex: string, alpha: number) => {
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${
    value & 255
  }, ${alpha})`;
};

const pad = (n: number) => ("0" + n).slice(-2);

/** Local calendar date of a Date, "YYYY-MM-DD". */
export const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Local midnight of "YYYY-MM-DD". */
export const isoToDate = (iso: string) => {
  const [year, month, day] = iso.split("-").map((n) => parseInt(n));
  return new Date(year, month - 1, day);
};

export const addDaysIso = (iso: string, days: number) => {
  const date = isoToDate(iso);
  // Day arithmetic via the date constructor, not milliseconds: a DST switch
  // inside the range would otherwise shift the result by an hour.
  return toIsoDate(
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
  );
};

/** 0 = Montag ... 6 = Sonntag */
export const weekDayOfIso = (iso: string) => (isoToDate(iso).getDay() + 6) % 7;

export const mondayOfIso = (iso: string) => addDaysIso(iso, -weekDayOfIso(iso));

/** ISO 8601 week number ("KW"). */
export const isoWeekNumber = (iso: string) => {
  // The ISO week belongs to the year of its Thursday.
  const thursday = isoToDate(addDaysIso(iso, 3 - weekDayOfIso(iso)));
  const firstOfYear = new Date(thursday.getFullYear(), 0, 1);
  const dayOfYear = Math.round(
    (thursday.getTime() - firstOfYear.getTime()) / 86400000
  );
  return Math.floor(dayOfYear / 7) + 1;
};

/** "05.10." */
export const shortDate = (iso: string) => {
  const [, month, day] = iso.split("-");
  return `${day}.${month}.`;
};

/**
 * Splits the slots of one room and day into lanes so overlapping courses are
 * drawn next to each other. Each cluster of mutually overlapping slots gets as
 * many lanes as it needs; slots that touch (one ends when the next starts) do
 * not overlap.
 */
export function assignLanes(slots: Array<TimetableSlot>): Array<PositionedSlot> {
  const sorted = slots
    .map((slot) => ({
      ...slot,
      startMin: strTimeToMin(slot.startTime),
      endMin: strTimeToMin(slot.endTime),
      lane: 0,
      lanes: 1,
    }))
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const result: Array<PositionedSlot> = [];
  let cluster: Array<PositionedSlot> = [];
  let laneEnds: Array<number> = [];
  let clusterEnd = -1;

  const closeCluster = () => {
    cluster.forEach((slot) => {
      slot.lanes = laneEnds.length;
    });
    result.push(...cluster);
    cluster = [];
    laneEnds = [];
  };

  sorted.forEach((slot) => {
    if (cluster.length > 0 && slot.startMin >= clusterEnd) closeCluster();
    let lane = laneEnds.findIndex((end) => end <= slot.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(slot.endMin);
    } else {
      laneEnds[lane] = slot.endMin;
    }
    slot.lane = lane;
    cluster.push(slot);
    clusterEnd = cluster.length === 1 ? slot.endMin : Math.max(clusterEnd, slot.endMin);
  });
  if (cluster.length > 0) closeCluster();
  return result;
}

/**
 * The visible time range in minutes, whole hours around all slots.
 */
export function timeRange(slots: Array<TimetableSlot>) {
  if (slots.length === 0) return { startMin: 8 * 60, endMin: 20 * 60 };
  let start = 24 * 60;
  let end = 0;
  slots.forEach((slot) => {
    start = Math.min(start, strTimeToMin(slot.startTime));
    end = Math.max(end, strTimeToMin(slot.endTime));
  });
  const startMin = Math.floor(start / 60) * 60;
  const endMin = Math.min(24 * 60, Math.max(Math.ceil(end / 60) * 60, startMin + 60));
  return { startMin, endMin };
}

export type LayoutOptions = {
  // a `locationKey`, or null for all rooms (club timetable)
  locationFilter: string | null;
  // every shown day, also an empty weekend - for "the next 7 days" on phones
  allDays: boolean;
};

/**
 * The time range the grid shows. Phones show one day at a time, so the axis
 * starts at that day's first course instead of the earliest course of the
 * week - no scrolling through empty hours. A day without courses only needs
 * room for "Keine Kurse". Wider screens show all days on one shared axis.
 *
 * @param days the drawn days
 * @param selectedDay the day shown on a phone
 * @param perDay true on phones (one day at a time)
 */
export function visibleTimeRange(
  days: Array<TimetableDay>,
  selectedDay: string,
  perDay: boolean
): { startMin: number; endMin: number } {
  const slotsOf = (list: Array<TimetableDay>) =>
    list.reduce<Array<PositionedSlot>>(
      (all, day) =>
        all.concat(...day.columns.map((column) => column.slots)),
      []
    );
  const week = timeRange(slotsOf(days));
  if (!perDay) return week;
  const daySlots = slotsOf(days.filter((day) => day.date === selectedDay));
  if (daySlots.length > 0) return timeRange(daySlots);
  return { startMin: week.startMin, endMin: week.startMin + 60 };
}

/**
 * Builds the day columns like the printed plan: Monday to Friday always,
 * Saturday and Sunday only when something takes place (unless `allDays`), and
 * per day one sub-column for each room that is used that day, in room order.
 *
 * @param data the route's response
 * @param options room filter and whether to keep empty weekend days
 */
export function layoutDays(
  data: TimetableData,
  options: LayoutOptions
): Array<TimetableDay> {
  const { locationFilter, allDays } = options;
  const slots = data.slots.filter(
    (slot) =>
      locationFilter === null || locationKey(slot.location) === locationFilter
  );

  const order = data.locations.map(locationKey);
  const rank = (key: string) => {
    const index = order.indexOf(key);
    return index === -1 ? order.length : index;
  };

  const days: Array<TimetableDay> = [];
  data.days.forEach((date) => {
    // from the date, not the position: a rolling range starts on any day
    const weekDay = weekDayOfIso(date);
    const daySlots = slots.filter((slot) => slot.date === date);
    if (!allDays && weekDay >= 5 && daySlots.length === 0) return;

    const byLocation: { [key: string]: Array<TimetableSlot> } = {};
    const locations: { [key: string]: TimetableLocation } = {};
    daySlots.forEach((slot) => {
      const key = locationKey(slot.location);
      if (!byLocation[key]) {
        byLocation[key] = [];
        locations[key] = slot.location;
      }
      byLocation[key].push(slot);
    });

    const keys = Object.keys(byLocation).sort((a, b) => rank(a) - rank(b));
    const columns: Array<TimetableColumn> =
      keys.length === 0
        ? [{ location: null, slots: [] }]
        : keys.map((key) => ({
            location: locations[key],
            slots: assignLanes(byLocation[key]),
          }));
    days.push({ date, weekDay, columns });
  });
  return days;
}
